import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { Close } from '@icon-park/react'

import { UpdateSpokespersonRequest } from '@shared/models'

import { Alert, Api } from '@/modules'

import { useInput, useRouting } from '@/hooks'

import { Button, Flex, Form, Input, Switch } from '@/components'
import { HorizontalTagInput } from '@/components/core/HorizontalTagInput/HorizontalTagInput.component'
import { TagOption } from '@/components/core/HorizontalTagInput/HorizontalTagInput.hooks'

import { Str } from '@/utils/strings'

import { useCompanyContext } from '../../CompanyDetails.context'

import { DialogContainer, DialogOverlay, ModalHeaderContainer } from './EditModal.styles'

import { Tokens } from '@/assets/tokens'

export const EditSpokespersonModal = memo(Component)

function Component() {
  const { companyID, currentPage, queryParams, changeQueryParam } = useRouting()
  const { company, allBrands, reloadAllBrands, reloadCompany, reloadSpokesPeople, spokespeople } = useCompanyContext()

  const [editSpokespersonId, setEditSpokespersonId] = useState<string | null>(null)
  const [editSpokespersonBrandId, setEditSpokespersonBrandId] = useState<string | null>(null)
  const [shouldScoreCenaInEditedBrand, setShouldScoreCenaInEditedBrand] = useState(false)
  const [hasCenaToggleChanged, setHasCenaToggleChanged] = useState(false)
  const [variations, setVariations] = useState<TagOption[]>([])
  const hydratedFormKeyRef = useRef<string | null>(null)

  const { sendAlert } = Alert.Hook()

  const { value: name, binder: nameBinder, reset: resetName } = useInput()

  const brandNameById = useMemo(() => {
    return new Map(allBrands.map((brand) => [brand.id, brand.name]))
  }, [allBrands])

  const editedBrandName = useMemo(() => {
    if (!editSpokespersonBrandId) return ''
    return brandNameById.get(editSpokespersonBrandId) ?? ''
  }, [editSpokespersonBrandId, brandNameById])

  const { isRunning: isUpdatingSpokesperson, run: updateSpokesperson } = Api.Command<void>(
    Api.Methods.PATCH,
    'spokespersons/:id',
  )

  useEffect(() => {
    const newEditId = queryParams.editSpokespersonId || null
    const newEditBrandId = queryParams.editSpokespersonBrandId || null
    setEditSpokespersonId(newEditId)
    setEditSpokespersonBrandId(newEditBrandId)
  }, [queryParams.editSpokespersonId, queryParams.editSpokespersonBrandId])

  useEffect(() => {
    if (!allBrands.length) {
      reloadAllBrands()
    }
  }, [allBrands.length, reloadAllBrands])

  useEffect(() => {
    if (!editSpokespersonId || !spokespeople) {
      return
    }

    const hydrationKey = `${editSpokespersonId}:${editSpokespersonBrandId || ''}`
    if (hydratedFormKeyRef.current === hydrationKey) {
      return
    }

    const spokesperson = spokespeople.elements.find((s) => s.id === editSpokespersonId)
    if (!spokesperson) return

    hydratedFormKeyRef.current = hydrationKey

    resetName(spokesperson.name)

    if (editSpokespersonBrandId) {
      setShouldScoreCenaInEditedBrand(spokesperson.cenaBrandIDs.includes(editSpokespersonBrandId))
    } else {
      setShouldScoreCenaInEditedBrand(false)
    }
    setHasCenaToggleChanged(false)

    const variationTags =
      spokesperson.variations?.map((variation, index) => ({
        id: `variation-${index}`,
        text: variation,
      })) || []
    setVariations(variationTags)
  }, [editSpokespersonId, editSpokespersonBrandId, spokespeople])

  function onSubmit(): void {
    if (Str.from(name).isBlank) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'Nome é obrigatório',
      })
    }

    if (editSpokespersonId) {
      const originalSpokesperson = spokespeople?.elements.find((s) => s.id === editSpokespersonId)
      if (!originalSpokesperson) return

      const input: UpdateSpokespersonRequest = {}

      if (name !== originalSpokesperson.name) {
        input.name = name
      }

      if (editSpokespersonBrandId && hasCenaToggleChanged) {
        const originalScoringBrandIDs = [...(originalSpokesperson.scoringBrandIDs || [])]
        const originalCenaBrandIDs = [...(originalSpokesperson.cenaBrandIDs || [])]

        const originalScoringSet = new Set(originalScoringBrandIDs)
        const nextCenaSet = new Set(originalCenaBrandIDs)
        if (shouldScoreCenaInEditedBrand && originalScoringSet.has(editSpokespersonBrandId)) {
          nextCenaSet.add(editSpokespersonBrandId)
        } else {
          nextCenaSet.delete(editSpokespersonBrandId)
        }

        const nextCenaBrandIDs = Array.from(nextCenaSet).sort()
        const sortedOriginalCenaBrandIDs = [...originalCenaBrandIDs].sort()
        if (JSON.stringify(nextCenaBrandIDs) !== JSON.stringify(sortedOriginalCenaBrandIDs)) {
          input.cenaBrandIDs = nextCenaBrandIDs
        }
      }

      const currentVariations = variations.map((tag) => tag.text)
      const originalVariations = originalSpokesperson.variations || []
      if (JSON.stringify(currentVariations.sort()) !== JSON.stringify(originalVariations.sort())) {
        input.variations = currentVariations
      }

      if (Object.keys(input).length > 0) {
        updateSpokesperson<UpdateSpokespersonRequest>({
          binds: [['id', editSpokespersonId]],
          input,
          onSuccess,
        })
      } else {
        handleCancelEdit()
      }
    }
  }

  function onSuccess() {
    sendAlert({
      type: 'success',
      title: 'Sucesso',
      message: 'Porta-voz atualizado com sucesso!',
    })
    closeModal()
    reloadSpokesPeople(currentPage)
    reloadCompany()
  }

  function handleCancelEdit() {
    closeModal()
  }

  function closeModal() {
    hydratedFormKeyRef.current = null
    setEditSpokespersonId(null)
    setEditSpokespersonBrandId(null)
    setShouldScoreCenaInEditedBrand(false)
    setHasCenaToggleChanged(false)
    setVariations([])
    changeQueryParam({ param: 'editSpokespersonId', value: undefined })
    changeQueryParam({ param: 'editSpokespersonBrandId', value: undefined })
  }

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleCancelEdit()
      }
    }

    if (editSpokespersonId) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [editSpokespersonId])

  if (!companyID || !company || !editSpokespersonId) return null

  return (
    <DialogOverlay>
      <DialogContainer>
        <ModalHeaderContainer $type="row" $justify="space-between" $align="stretch" $gap={20} $fit>
          <h1>Atualizar Porta-voz</h1>
          <Button.Root $variant="link" onClick={handleCancelEdit}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </ModalHeaderContainer>
        <Form onSubmit={onSubmit}>
          <Input label="Nome" placeholder="Nome" {...nameBinder} required />
          <Input label="Marca" value={editedBrandName || 'Sem marca associada'} disabled onChange={() => undefined} />
          {editSpokespersonBrandId && (
            <Switch.Root
              isChecked={shouldScoreCenaInEditedBrand}
              toggle={() => {
                setShouldScoreCenaInEditedBrand((current) => !current)
                setHasCenaToggleChanged(true)
              }}
              label="Pontuar no CENA nesta marca"
              direction="row"
            />
          )}
          <HorizontalTagInput
            label="Variações"
            placeholder="Digite variações e pressione Enter"
            maxCharacters={200}
            preSelectedTags={variations}
            onChange={setVariations}
            disabled={isUpdatingSpokesperson}
          />
          <Flex $type="row" $justify="flex-end" $align="stretch" $gap={20} $fit>
            <Button.Root
              type="button"
              $size={114}
              onClick={handleCancelEdit}
              disabled={isUpdatingSpokesperson}
              $loading={isUpdatingSpokesperson}>
              <Button.Text value="Cancelar" />
            </Button.Root>
            <Button.Root
              type="submit"
              $size={114}
              $variant="primary"
              disabled={isUpdatingSpokesperson}
              $loading={isUpdatingSpokesperson}>
              <Button.Text value="Salvar" />
            </Button.Root>
          </Flex>
        </Form>
      </DialogContainer>
    </DialogOverlay>
  )
}
